/**
 * 设备相关工具函数
 */

/**
 * 获取设备ID (浏览器指纹)
 */
export function getDeviceId(): string {
  // 从 localStorage 获取或生成新的设备ID
  let deviceId = localStorage.getItem('deviceId')

  if (!deviceId) {
    // 使用浏览器特征生成指纹
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('fingerprint', 2, 2)
    }
    const canvasData = canvas.toDataURL()

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvasData,
    ].join('|')

    // 简单哈希
    let hash = 0
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash
    }

    deviceId = Math.abs(hash).toString(36)
    localStorage.setItem('deviceId', deviceId)
  }

  return deviceId
}

/**
 * 获取当前平台信息
 *
 * 平台类型说明:
 * - EXE: 电脑 EXE 客户端
 * - FLEX: Flash 客户端
 * - IOS: iOS 原生应用
 * - ANDROID: Android 原生应用
 * - H5IOS: H5 在 iOS 浏览器中运行
 * - H5ANDROID: H5 在 Android 浏览器中运行
 * - H5HARMONY: H5 在鸿蒙系统浏览器中运行
 * - H5COMPUTER: H5 在电脑浏览器中运行
 */
export function getPlatform(): string {
  const userAgent = navigator.userAgent.toLowerCase()

  // 检测是否在鸿蒙系统 (HarmonyOS 会在 UA 中包含 harmony 或 openharmony)
  if (/harmonyos|openharmony/i.test(userAgent)) {
    return 'H5HARMONY'
  }

  // 检测 iOS 设备 (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/i.test(userAgent)) {
    return 'H5IOS'
  }

  // 检测 Android 设备
  if (/android/i.test(userAgent)) {
    return 'H5ANDROID'
  }

  // 检测桌面系统 (Windows, Mac, Linux)
  if (/windows|macintosh|mac os x|linux/i.test(userAgent)) {
    // 进一步判断是否是平板模式 (iPad 可能伪装成 Mac)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    // iPad 在 iPadOS 13+ 中会伪装成 Mac,需要额外判断
    if (/macintosh/i.test(userAgent) && isTouchDevice) {
      return 'H5IOS' // 这是伪装成 Mac 的 iPad
    }

    return 'H5COMPUTER'
  }

  // 默认返回 H5COMPUTER
  return 'H5COMPUTER'
}

/**
 * 判断是否是移动设备
 */
export function isMobile(): boolean {
  const platform = getPlatform()
  return ['H5IOS', 'H5ANDROID', 'H5HARMONY'].includes(platform)
}

/**
 * 判断是否是桌面设备
 */
export function isDesktop(): boolean {
  return getPlatform() === 'H5COMPUTER'
}

/**
 * 获取操作系统类型
 */
export function getOS(): 'ios' | 'android' | 'harmony' | 'windows' | 'mac' | 'linux' | 'unknown' {
  const userAgent = navigator.userAgent.toLowerCase()

  if (/harmonyos|openharmony/i.test(userAgent)) return 'harmony'
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios'
  if (/android/i.test(userAgent)) return 'android'
  if (/windows/i.test(userAgent)) return 'windows'
  if (/macintosh|mac os x/i.test(userAgent)) return 'mac'
  if (/linux/i.test(userAgent)) return 'linux'

  return 'unknown'
}

/**
 * 设备类型
 * - pc: 桌面端 (宽度 > 1024px)
 * - ipad: 平板设备 (宽度 768-1024px)
 * - mobile-landscape: 手机横屏 (宽度 < 768px 且高度 <= 500px)
 * - mobile-portrait: 手机竖屏 (宽度 < 768px 且高度 > 500px)
 */
export type DeviceType = 'pc' | 'ipad' | 'mobile-landscape' | 'mobile-portrait'

/**
 * 屏幕方向
 */
export type Orientation = 'landscape' | 'portrait'

/**
 * 全局设备检测函数类型声明 (定义在 index.html 中)
 */
declare global {
  interface Window {
    __getDeviceType?: () => DeviceType
  }
}

/**
 * 获取当前设备类型
 *
 * 优先使用全局函数 window.__getDeviceType (定义在 index.html 中)
 * 这样可以确保 index.html 的 rem 设置和 Vue 应用中的设备检测逻辑完全一致
 */
export function getDeviceType(): DeviceType {
  // 如果存在全局设备检测函数,优先使用它
  if (typeof window !== 'undefined' && window.__getDeviceType) {
    return window.__getDeviceType()
  }

  // 降级处理:如果全局函数不存在,使用本地实现
  // (这种情况一般不会发生,因为 index.html 会先加载)
  const ua = navigator.userAgent
  const width = window.innerWidth
  const height = window.innerHeight
  const shortSide = Math.min(width, height)
  const longSide = Math.max(width, height)
  const isLandscape = width > height
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // ========== 1. UA 优先判断 (可靠场景) ==========

  // iPhone
  if (/iPhone/i.test(ua)) {
    return isLandscape ? 'mobile-landscape' : 'mobile-portrait'
  }

  // Android 手机 (有 Mobile 关键字)
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) {
    return isLandscape ? 'mobile-landscape' : 'mobile-portrait'
  }

  // Android 平板 (Android 但没有 Mobile)
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) {
    return 'ipad'
  }

  // ========== 2. 特殊处理：iPad 伪装成 Mac ==========

  // iPadOS 13+ 伪装成 Mac，但有触摸能力
  if (/Macintosh/i.test(ua) && isTouchDevice) {
    return 'ipad'
  }

  // ========== 3. 桌面端判断 ==========

  // 真正的桌面端：Mac/Windows 且无触摸
  if (/Macintosh|Windows/i.test(ua) && !isTouchDevice) {
    return 'pc'
  }

  // Windows 触屏设备：根据屏幕尺寸判断
  if (/Windows/i.test(ua) && isTouchDevice) {
    // 小屏幕触屏 Windows 设备可能是平板
    if (shortSide < 600 && longSide < 1024) {
      return isLandscape ? 'mobile-landscape' : 'mobile-portrait'
    }
    if (shortSide >= 600 && longSide <= 1366) {
      return 'ipad' // Windows 平板
    }
    return 'pc' // 触屏笔记本/一体机
  }

  // ========== 4. 尺寸兜底 (其他未知设备) ==========

  if (shortSide < 600) {
    return isLandscape ? 'mobile-landscape' : 'mobile-portrait'
  }

  if (shortSide >= 600 && longSide <= 1366) {
    return 'ipad'
  }

  return 'pc'
}

/**
 * 获取屏幕方向
 */
export function getOrientation(): Orientation {
  const width = window.innerWidth
  const height = window.innerHeight
  return height > width ? 'portrait' : 'landscape'
}

/**
 * 判断是否是手机横屏
 */
export function isMobileLandscape(): boolean {
  return getDeviceType() === 'mobile-landscape'
}

/**
 * 判断是否是手机竖屏
 */
export function isMobilePortrait(): boolean {
  return getDeviceType() === 'mobile-portrait'
}

/**
 * 判断是否是PC端
 */
export function isPC(): boolean {
  return getDeviceType() === 'pc'
}

/**
 * 判断是否是 iPad (平板)
 */
export function isIPad(): boolean {
  return getDeviceType() === 'ipad'
}

/**
 * 设备信息
 */
export interface DeviceInfo {
  /** 设备类型 */
  type: DeviceType
  /** 屏幕方向 */
  orientation: Orientation
  /** 是否是移动设备 */
  isMobile: boolean
  /** 是否是桌面设备 */
  isDesktop: boolean
  /** 操作系统 */
  os: ReturnType<typeof getOS>
  /** 平台 */
  platform: string
  /** 屏幕宽度 */
  screenWidth: number
  /** 屏幕高度 */
  screenHeight: number
}

/**
 * 获取完整的设备信息
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    type: getDeviceType(),
    orientation: getOrientation(),
    isMobile: isMobile(),
    isDesktop: isDesktop(),
    os: getOS(),
    platform: getPlatform(),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  }
}
